import { Link } from "react-router-dom";
import useRevealOnScroll from "./useRevealOnScroll";
import {
  AIO_PROGRAM,
  AIO_AUDIENCES,
  AIO_WHY,
  AIO_COMPARE,
  AIO_APP_BANNER,
  AIO_TRUST,
  AIO_STEPS,
  AIO_MARQUEE,
  AIO_FINAL_CTA,
} from "./landingAllInOneContent";
import { LANDING_SCREEN } from "./landingAssets";
import { TRIAL_DAYS } from "../../../constants/trial";
import s from "./homeLanding.module.css";

function Reveal({
  children,
  className = "",
  delay = 0,
  motion = "up",
  fill = false,
}) {
  const [ref, visible] = useRevealOnScroll({
    threshold: 0.12,
    rootMargin: "0px 0px -4% 0px",
  });
  const motionClass =
    motion === "left"
      ? s.aioRevealLeft
      : motion === "right"
        ? s.aioRevealRight
        : motion === "scale"
          ? s.aioRevealScale
          : motion === "fly"
            ? s.aioRevealFly
            : motion === "flyLeft"
              ? s.aioRevealFlyLeft
              : motion === "flyRight"
                ? s.aioRevealFlyRight
                : s.aioRevealUp;

  return (
    <div
      ref={ref}
      className={`${s.aioReveal} ${motionClass} ${fill ? s.aioRevealFill : ""} ${
        visible ? s.aioRevealIn : ""
      } ${className}`.trim()}
      style={delay ? { "--aio-delay": `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}

function AudienceIcon({ name }) {
  const common = {
    viewBox: "0 0 48 48",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
  };

  switch (name) {
    case "whistle":
      return (
        <svg {...common}>
          <path
            d="M10 22c0-5.5 4.5-10 10-10h4c3.3 0 6 2.7 6 6v0c0 2.2-1.8 4-4 4h-2"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <circle cx="30" cy="28" r="8" stroke="currentColor" strokeWidth="2.4" />
          <circle cx="30" cy="28" r="3" fill="currentColor" />
          <path
            d="M14 28h6"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      );
    case "ball":
      return (
        <svg {...common}>
          <circle cx="24" cy="24" r="14" stroke="currentColor" strokeWidth="2.4" />
          <path
            d="M24 10c3 4 3 10 0 14s-3 10 0 14"
            stroke="currentColor"
            strokeWidth="2.2"
          />
          <path
            d="M11 18h26M11 30h26"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      );
    case "glove":
      return (
        <svg {...common}>
          <path
            d="M16 20c0-5 3.5-9 8-9s8 4 8 9v6c0 6-3.5 11-8 11s-8-5-8-11v-6z"
            stroke="currentColor"
            strokeWidth="2.4"
          />
          <path
            d="M16 24h16M20 16v4M28 16v4"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
          <path
            d="M18 36h12"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      );
    case "barbell":
      return (
        <svg {...common}>
          <path
            d="M8 24h32M12 18v12M36 18v12M16 20v8M32 20v8"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
          <rect
            x="6"
            y="16"
            width="4"
            height="16"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="2"
          />
          <rect
            x="38"
            y="16"
            width="4"
            height="16"
            rx="1.5"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      );
    case "ribbon":
      return (
        <svg {...common}>
          <path
            d="M24 8c8 6 12 12 12 18a12 12 0 1 1-24 0c0-6 4-12 12-18z"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          <path
            d="M24 26v14M18 36l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "nodes":
      return (
        <svg {...common}>
          <circle cx="14" cy="16" r="5" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="34" cy="16" r="5" stroke="currentColor" strokeWidth="2.2" />
          <circle cx="24" cy="34" r="5" stroke="currentColor" strokeWidth="2.2" />
          <path
            d="M18 19l4 10M30 19l-4 10M19 16h10"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return null;
  }
}

function ProgramIcon({ name }) {
  const common = {
    viewBox: "0 0 32 32",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
  };
  switch (name) {
    case "users":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
          <circle cx="22" cy="13" r="3" stroke="currentColor" strokeWidth="2" />
          <path
            d="M5 24c1.5-4 4-6 7-6s5.5 2 7 6M18 24c.8-2.2 2.2-3.5 4-3.5 1.4 0 2.5.7 3.4 2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      );
    case "calendar":
      return (
        <svg {...common}>
          <rect x="5" y="7" width="22" height="18" rx="3" stroke="currentColor" strokeWidth="2" />
          <path d="M5 13h22M11 5v4M21 5v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "funnel":
      return (
        <svg {...common}>
          <path
            d="M7 8h18l-6 8v7l-6-3v-4L7 8z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "phone":
      return (
        <svg {...common}>
          <rect x="10" y="4" width="12" height="24" rx="3" stroke="currentColor" strokeWidth="2" />
          <path d="M14 24h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      );
    case "feed":
      return (
        <svg {...common}>
          <rect x="6" y="6" width="20" height="6" rx="2" stroke="currentColor" strokeWidth="2" />
          <rect x="6" y="15" width="20" height="6" rx="2" stroke="currentColor" strokeWidth="2" />
          <rect x="6" y="24" width="12" height="3" rx="1.5" fill="currentColor" />
        </svg>
      );
    case "wallet":
      return (
        <svg {...common}>
          <rect x="5" y="9" width="22" height="15" rx="3" stroke="currentColor" strokeWidth="2" />
          <path d="M5 14h22" stroke="currentColor" strokeWidth="2" />
          <circle cx="22" cy="19" r="1.8" fill="currentColor" />
        </svg>
      );
    default:
      return null;
  }
}

export default function LandingAllInOneSections({ onTryClick, showFinalCta = true }) {
  const marqueeLoop = [...AIO_MARQUEE.items, ...AIO_MARQUEE.items];

  return (
    <>
      {/* 1. Сплит с экраном — сразу другой ритм после героя */}
      <section id={AIO_APP_BANNER.id} className={s.aioInnerPad}>
        <div className={s.aioAppBanner}>
          <Reveal motion="left" className={s.aioAppBannerCopy}>
            <h2 className={s.aioAppBannerTitle}>
              <span className={s.aioAccentViolet}>{AIO_APP_BANNER.titleAccent}</span>{" "}
              {AIO_APP_BANNER.titleRest}
            </h2>
            <p>{AIO_APP_BANNER.lead}</p>
            <div className={s.aioChipRow}>
              {AIO_APP_BANNER.callouts.map((c) => (
                <span
                  key={c.id}
                  className={`${s.aioFeatureChip} ${
                    c.tone === "orange" ? s.aioFeatureChipOrange : ""
                  }`}
                >
                  {c.label}
                </span>
              ))}
            </div>
            <Link
              to="/register-clinic"
              className={s.aioPillCta}
              onClick={onTryClick}
            >
              <span>{AIO_APP_BANNER.cta}</span>
            </Link>
          </Reveal>
          <Reveal motion="fly" delay={140} className={s.aioAppBannerVisual}>
            <img
              className={`${s.aioAppPhoneFloat} ${s.aioScreenFly}`}
              src={AIO_APP_BANNER.src}
              alt={AIO_APP_BANNER.alt}
              loading="lazy"
              decoding="async"
            />
          </Reveal>
        </div>
      </section>

      {/* 2. Кому подойдёт — иконки, не ещё один текстовый грид */}
      <section id={AIO_AUDIENCES.id} className={s.aioAudienceBand}>
        <div className={s.aioInner}>
          <Reveal>
            <h2 className={s.aioSectionTitle}>
              {AIO_AUDIENCES.titleBefore}{" "}
              <span className={s.aioAccentViolet}>{AIO_AUDIENCES.titleAccent}</span>
            </h2>
            <p className={s.aioWhyLead}>{AIO_AUDIENCES.lead}</p>
          </Reveal>
          <div className={s.aioAudienceGrid}>
            {AIO_AUDIENCES.items.map((item, i) => (
              <Reveal key={item.id} delay={i * 70} motion="scale" fill>
                <article
                  className={`${s.aioAudienceCard} ${
                    item.tone === "orange" ? s.aioAudienceCardOrange : ""
                  }`}
                >
                  <span
                    className={`${s.aioAudienceIcon} ${
                      item.tone === "orange" ? s.aioAudienceIconOrange : ""
                    }`}
                    aria-hidden
                  >
                    <AudienceIcon name={item.icon} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Креатив: бегущая лента видов спорта */}
      <section id={AIO_MARQUEE.id} className={s.aioMarqueeBand} aria-label={AIO_MARQUEE.titleBefore}>
        <div className={s.aioInner}>
          <Reveal>
            <h2 className={s.aioSectionTitle}>
              {AIO_MARQUEE.titleBefore}{" "}
              <span className={s.aioAccentOrange}>{AIO_MARQUEE.titleAccent}</span>
            </h2>
            <p className={s.aioWhyLead}>{AIO_MARQUEE.lead}</p>
          </Reveal>
        </div>
        <div className={s.aioMarqueeViewport}>
          <div className={s.aioMarqueeTrack}>
            {marqueeLoop.map((label, i) => (
              <span
                key={`${label}-${i}`}
                className={`${s.aioMarqueeChip} ${
                  i % 2 === 0 ? s.aioMarqueeChipViolet : s.aioMarqueeChipOrange
                }`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Было → стало */}
      <section className={s.aioCompareBand}>
        <div className={s.aioInner}>
          <Reveal>
            <h2 className={s.aioSectionTitle}>
              {AIO_COMPARE.titleBefore}{" "}
              <span className={s.aioAccentOrange}>{AIO_COMPARE.titleAccent}</span>
            </h2>
            <p className={s.aioWhyLead}>{AIO_COMPARE.lead}</p>
          </Reveal>
          <div className={s.aioCompareList}>
            {AIO_COMPARE.rows.map((row, i) => (
              <Reveal key={row.id} delay={i * 80} motion="left">
                <article className={s.aioCompareRow}>
                  <div className={s.aioCompareBefore}>
                    <span>Было</span>
                    <strong>{row.before}</strong>
                  </div>
                  <div className={s.aioCompareArrow} aria-hidden>
                    →
                  </div>
                  <div className={s.aioCompareAfter}>
                    <span>Стало</span>
                    <strong>{row.after}</strong>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Шаги запуска */}
      <section id="steps" className={s.aioWhiteBand}>
        <div className={s.aioInner}>
          <Reveal>
            <h2 className={s.aioSectionTitle}>{AIO_STEPS.title}</h2>
          </Reveal>
          <div className={s.aioStepsGrid}>
            {AIO_STEPS.items.map((step, i) => (
              <Reveal key={step.title} delay={i * 100} motion="scale" fill>
                <article className={s.aioStepCard}>
                  <span className={s.aioStepNum}>{step.num}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Почему одна система + экран */}
      <section className={s.aioInnerPad}>
        <div className={s.aioBrandPanel}>
          <Reveal motion="left" className={s.aioBrandPanelCopy}>
            <h2 className={s.aioSectionTitleLeft}>
              {AIO_WHY.titleBefore}{" "}
              <span className={s.aioAccentOrange}>{AIO_WHY.titleAccent}</span>
            </h2>
            <ul className={s.aioBrandPoints}>
              {AIO_WHY.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
            <Link
              to="/register-clinic"
              className={s.aioPillCta}
              onClick={onTryClick}
            >
              <span>{AIO_WHY.cta}</span>
            </Link>
          </Reveal>
          <Reveal motion="flyRight" delay={120} className={s.aioBrandPanelShot}>
            <img
              className={s.aioScreenFly}
              src={LANDING_SCREEN.ownerJournal}
              alt="Журнал клуба"
              loading="lazy"
              decoding="async"
            />
          </Reveal>
        </div>
      </section>

      {/* 7. Возможности с иконками */}
      <section id="capabilities" className={s.aioProgramSection}>
        <Reveal>
          <h2 className={s.aioSectionTitle}>{AIO_PROGRAM.title}</h2>
        </Reveal>
        <div className={s.aioProgramGrid}>
          {AIO_PROGRAM.slides.map((slide, i) => (
            <Reveal
              key={slide.id}
              delay={(i % 3) * 90 + Math.floor(i / 3) * 40}
              fill
            >
              <article className={s.aioProgramCard}>
                <span className={s.aioProgramIcon} aria-hidden>
                  <ProgramIcon name={slide.icon} />
                </span>
                <h3 className={s.aioProgramCardTitle}>{slide.title}</h3>
                <ul className={s.aioProgramList}>
                  {slide.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* 8. Доверие */}
      <section className={s.aioInnerPad}>
        <Reveal>
          <h2 className={s.aioSectionTitle}>
            {AIO_TRUST.titleBefore}{" "}
            <span className={s.aioAccentOrange}>{AIO_TRUST.titleAccent}</span>
          </h2>
        </Reveal>
        <div className={s.aioTrustStrip}>
          {AIO_TRUST.items.map((item, i) => (
            <Reveal key={item.id} delay={i * 90} motion="up" fill>
              <article className={s.aioTrustCard}>
                <span className={s.aioTrustIcon} aria-hidden>
                  ✓
                </span>
                <strong>{item.title}</strong>
                <p>{item.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {showFinalCta ? (
        <section className={s.aioFinalBand}>
          <Reveal motion="scale">
            <div className={s.aioFinalBox}>
              <h2 className={s.aioSectionTitle}>{AIO_FINAL_CTA.title}</h2>
              <p className={s.aioWhyLead}>{AIO_FINAL_CTA.lead}</p>
              <div className={s.aioFinalActions}>
                <Link
                  to="/register-clinic"
                  className={s.aioPillCta}
                  onClick={onTryClick}
                >
                  <span>{AIO_FINAL_CTA.primary}</span>
                </Link>
                <Link to="/find" className={s.aioGhostCta}>
                  {AIO_FINAL_CTA.secondary}
                </Link>
              </div>
              <p className={s.aioFinalNote}>
                {TRIAL_DAYS} дней бесплатно · без карты · регистрация за 5 минут
              </p>
            </div>
          </Reveal>
        </section>
      ) : null}
    </>
  );
}
